// ── 매출 변경이력 diff 렌더러 ──────────────────────────────────────────────────
import React from 'react';
import { fmtDate } from './styles';
import { TABLE_FIELD_LABELS, auditBaseTable } from './auditFieldLabels';

/** action별 색상 설정 */
export const ACTION_CFG = {
  create:       { label: '등록',       color: '#15803d', bg: '#dcfce7', border: '#22c55e' },
  update:       { label: '수정',       color: '#1d4ed8', bg: '#dbeafe', border: '#3b82f6' },
  delete:       { label: '삭제',       color: '#b91c1c', bg: '#fee2e2', border: '#ef4444' },
  upload:       { label: '파일 업로드', color: '#7c3aed', bg: '#f3e8ff', border: '#8b5cf6' },
  download:     { label: 'CSV 다운로드', color: '#0369a1', bg: '#e0f2fe', border: '#38bdf8' },
  batch_delete: { label: '배치 삭제',  color: '#b91c1c', bg: '#fee2e2', border: '#ef4444' },
};

/** 값을 사람이 읽기 쉬운 문자열로 변환 */
function displayVal(v) {
  if (v == null || v === '') return null;
  // 숫자처럼 생긴 금액 필드는 콤마 포맷 (5자리 이상이면 금액으로 간주)
  const n = Number(v);
  if (Number.isFinite(n) && !isNaN(n) && String(v).length >= 5 && !/[-.]/.test(String(v))) {
    return n.toLocaleString('ko-KR');
  }
  return String(v);
}

/** 칸 비교로 안 보이는 기록을 문장으로 — 정산서에서 매장 연결·해제, 사람이 적은 설명 */
function auditNoteLines(old, nxt) {
  const lines = [];
  const how = [nxt.link_reason, nxt.via].filter(Boolean).join(' · ');
  if (nxt.unlinked_reason) {
    lines.push(`매장 연결을 해제했습니다${nxt.client_name ? ` (${nxt.client_name})` : ''}. 사유: ${nxt.unlinked_reason}`);
  } else if (nxt.client_name && nxt.client_name !== old.client_name) {
    lines.push(old.client_name
      ? `매장을 "${old.client_name}" 에서 "${nxt.client_name}" 으로 바꿨습니다.`
      : `매장 "${nxt.client_name}" 에 연결했습니다.`);
  }
  if (how && lines.length) lines[lines.length - 1] += ` (${how})`;
  const summary = nxt.summary || old.summary;
  if (summary) lines.push(summary);
  return lines;
}

/**
 * 이력 항목 한 건의 변경 내용을 렌더링 (기본 테이블용)
 * create: [필드명] [등록된 값] (파란색)
 * delete: [필드명] [삭제된 값] (빨간색)
 * update: [필드명] [이전 값] → [새 값] (변경된 필드만)
 */
export function renderDiff(row, tableName) {
  const labels = TABLE_FIELD_LABELS[auditBaseTable(tableName)] || {};
  const old = row.old_values || {};
  const nxt = row.new_values || {};

  let entries = [];

  if (row.action === 'create') {
    entries = Object.entries(nxt)
      .filter(([k, v]) => labels[k] && v != null && v !== '')
      .map(([k, v]) => ({ label: labels[k], oldVal: null, newVal: displayVal(v) }));
  } else if (row.action === 'delete') {
    entries = Object.entries(old)
      .filter(([k, v]) => labels[k] && v != null && v !== '')
      .map(([k, v]) => ({ label: labels[k], oldVal: displayVal(v), newVal: null }));
  } else {
    const allKeys = [...new Set([...Object.keys(old), ...Object.keys(nxt)])];
    entries = allKeys
      .filter((k) => labels[k] && String(old[k] ?? '') !== String(nxt[k] ?? ''))
      .map((k) => ({ label: labels[k], oldVal: displayVal(old[k]), newVal: displayVal(nxt[k]) }));
  }

  const notes = auditNoteLines(old, nxt);
  if (!entries.length && !notes.length) return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.action === 'update' ? '변경 내용 없음' : '내용 없음'}</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {notes.map((line, i) => (
        <div key={`note-${i}`} style={{ fontSize: 12, color: 'var(--text-sub)', padding: '0 2px', whiteSpace: 'pre-wrap' }}>{line}</div>
      ))}
      {entries.map(({ label, oldVal, newVal }, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 0, fontSize: 12,
          background: 'var(--bg-subtle)', borderRadius: 6, padding: '5px 10px', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-muted)', fontWeight: 600, minWidth: 72, flexShrink: 0, marginRight: 8 }}>{label}</span>
          {row.action === 'create' && newVal != null && (
            <span style={{ color: '#1d4ed8', fontWeight: 700 }}>{newVal}</span>
          )}
          {row.action === 'delete' && oldVal != null && (
            <span style={{ color: '#b91c1c', fontWeight: 700 }}>{oldVal}</span>
          )}
          {row.action === 'update' && (
            <>
              {oldVal != null
                ? <span style={{ color: '#b91c1c', background: '#fee2e2', borderRadius: 4, padding: '1px 6px', marginRight: 6 }}>{oldVal}</span>
                : <span style={{ color: 'var(--text-muted)', marginRight: 6, fontStyle: 'italic' }}>없음</span>
              }
              <span style={{ color: 'var(--text-muted)', marginRight: 6, fontSize: 13, fontWeight: 700 }}>→</span>
              {newVal != null
                ? <span style={{ color: '#15803d', background: '#dcfce7', borderRadius: 4, padding: '1px 6px' }}>{newVal}</span>
                : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>없음</span>
              }
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * 배치 이벤트(업로드/삭제) 렌더러 — 카드승인·세금계산서 공용
 * summary 필드를 쉬운 한국어 문장으로 표시
 */
export function renderBatchDiff(row) {
  const nxt = row.new_values || {};
  const old = row.old_values || {};
  const vals = row.action === 'batch_delete' ? old : nxt;
  const lines = [];

  if (row.action === 'upload') {
    lines.push(`📤 파일명: ${vals.fileName || '-'}`);
    lines.push(`전체 ${vals.totalRows || 0}건 — 신규 ${vals.newCount || 0}건 / 갱신 ${vals.updatedCount || 0}건 / 스킵 ${vals.skippedCount || 0}건`);
  } else if (row.action === 'batch_delete') {
    lines.push(`🗑️ 삭제된 파일: ${vals.fileName || '-'}`);
    lines.push(`원래 ${vals.totalRows || 0}건 (업로더: ${vals.uploadedBy || '-'})`);
    if (vals.summary) lines.push(vals.summary);
  } else if (row.action === 'download') {
    lines.push(`📥 내려받은 파일: ${vals.fileName || '-'} (${vals.totalRows || 0}건)`);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {lines.map((line, i) => (
        <div key={i} style={{ fontSize: 12, color: 'var(--text-sub)', padding: '0 2px', whiteSpace: 'pre-wrap' }}>{line}</div>
      ))}
    </div>
  );
}

/**
 * 입금 이력 전용 렌더러
 * payment_ctx(서버 enrichment) + old/new_values를 조합해 자연어 문장으로 표시
 */
export function renderPaymentDiff(row) {
  const ctx = row.payment_ctx || {};
  const old = row.old_values  || {};
  const nxt = row.new_values  || {};

  // 입금 요약 헤더
  const ctxDate   = ctx.payment_date || fmtDate(old.payment_date) || '-';
  const ctxPayer  = ctx.payer_name   || old.payer_name  || '-';
  const ctxAmount = ctx.amount != null ? `${Number(ctx.amount).toLocaleString('ko-KR')}원` : (old.amount != null ? `${Number(old.amount).toLocaleString('ko-KR')}원` : '-');
  const ctxUser   = ctx.user_name    || old.userId      || '';
  const ctxLine   = [ctxDate, ctxPayer, ctxAmount, ctxUser].filter(Boolean).join('  ·  ');

  const lines = [];

  if (row.action === 'create') {
    lines.push('새 입금 내역이 등록됐습니다.');
    if (nxt.taxInvoiceId) lines.push(`📎 세금계산서 연결: ${nxt.taxInvoiceId}`);
  } else if (row.action === 'delete') {
    lines.push('이 입금 내역이 삭제됐습니다.');
  } else {
    const FIELD_SENTENCE = {
      payment_date: (o, n) => `입금일자를 ${fmtDate(o)} 에서 ${fmtDate(n)} 으로 변경했습니다.`,
      payer_name:   (o, n) => `입금자(상호)를 "${o}" 에서 "${n}" 으로 변경했습니다.`,
      amount:       (o, n) => `금액을 ${Number(o).toLocaleString('ko-KR')}원 에서 ${Number(n).toLocaleString('ko-KR')}원 으로 수정했습니다.`,
      note:         (o, n) => `비고를 "${o || '(없음)'}" 에서 "${n || '(없음)'}" 으로 변경했습니다.`,
    };
    const oldInv = old.taxInvoiceId;
    const newInv = nxt.taxInvoiceId;
    if (oldInv !== newInv) {
      if (!oldInv && newInv) lines.push(`📎 세금계산서를 연결했습니다: ${newInv}`);
      else if (oldInv && !newInv) lines.push(`🔗 세금계산서 연결이 해제됐습니다. (해제 전: ${oldInv})`);
      else if (oldInv && newInv) lines.push(`📎 세금계산서를 변경했습니다.\n   이전: ${oldInv}\n   변경: ${newInv}`);
    }
    const oldUser = old.userId || old.user_id;
    const newUser = nxt.userId || nxt.user_id;
    // 기록에 마케터 칸이 없으면 안 바꾼 것 — 지운 것처럼 보이면 안 된다
    if (('userId' in nxt || 'user_id' in nxt) && oldUser !== newUser && (oldUser || newUser)) {
      lines.push(`담당 마케터를 "${oldUser || '(없음)'}" 에서 "${newUser || '(없음)'}" 으로 변경했습니다.`);
    }
    for (const [key, fn] of Object.entries(FIELD_SENTENCE)) {
      const o = old[key]; const n = nxt[key];
      if (o !== undefined && n !== undefined && String(o) !== String(n)) lines.push(fn(o, n));
    }
    lines.push(...auditNoteLines(old, nxt));
    if (!lines.length) lines.push('변경 내용 없음');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 12, color: 'var(--text-sub)', fontWeight: 600, background: 'var(--bg-subtle)', borderRadius: 6, padding: '4px 10px', letterSpacing: '0.01em' }}>
        {ctxLine}
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ fontSize: 12, color: 'var(--text-sub)', padding: '0 2px', whiteSpace: 'pre-wrap' }}>{line}</div>
      ))}
    </div>
  );
}

/**
 * 세금계산서 이력 전용 렌더러
 * invoice_ctx(서버 enrichment) + old/new_values를 조합해 자연어 문장으로 표시
 */
export function renderTaxInvoiceDiff(row) {
  const ctx = row.invoice_ctx || {};
  const old = row.old_values  || {};
  const nxt = row.new_values  || {};

  // 세금계산서 요약 헤더
  const ctxDate     = ctx.issue_date    || fmtDate(old.issue_date)    || '-';
  const ctxCustomer = ctx.customer_name || old.customer_name          || '-';
  const ctxAmount   = ctx.total_amount != null
    ? `${Number(ctx.total_amount).toLocaleString('ko-KR')}원`
    : (old.total_amount != null ? `${Number(old.total_amount).toLocaleString('ko-KR')}원` : '-');
  const ctxType  = ctx.issue_type || old.issue_type || '';
  const ctxUser  = ctx.user_name  || old.userId     || '';
  const ctxLine  = [ctxDate, ctxCustomer, ctxAmount, ctxType, ctxUser].filter(Boolean).join('  ·  ');

  const lines = [];

  if (row.action === 'create') {
    lines.push('새 세금계산서가 등록됐습니다.');
    if (nxt.issue_type)    lines.push(`📄 유형: ${nxt.issue_type}`);
    if (nxt.customer_name) lines.push(`🏢 상호: ${nxt.customer_name}`);
    if (nxt.total_amount != null)
      lines.push(`💰 합계금액: ${Number(nxt.total_amount).toLocaleString('ko-KR')}원`
        + (nxt.supply_amount != null ? `  (공급가액 ${Number(nxt.supply_amount).toLocaleString('ko-KR')}원 + 세액 ${Number(nxt.tax_amount || 0).toLocaleString('ko-KR')}원)` : ''));
    if (nxt.issue_date) lines.push(`📅 발행일: ${fmtDate(nxt.issue_date)}`);
    if (nxt.userId)     lines.push(`👤 마케터: ${nxt.userId}`);
    if (nxt.note)       lines.push(`📝 비고: ${nxt.note}`);
  } else if (row.action === 'delete') {
    lines.push('이 세금계산서가 삭제됐습니다.');
    if (old.issue_type)    lines.push(`📄 유형: ${old.issue_type}`);
    if (old.customer_name) lines.push(`🏢 상호: ${old.customer_name}`);
    if (old.total_amount != null)
      lines.push(`💰 합계금액: ${Number(old.total_amount).toLocaleString('ko-KR')}원`
        + (old.supply_amount != null ? `  (공급가액 ${Number(old.supply_amount).toLocaleString('ko-KR')}원 + 세액 ${Number(old.tax_amount || 0).toLocaleString('ko-KR')}원)` : ''));
    if (old.issue_date) lines.push(`📅 발행일: ${fmtDate(old.issue_date)}`);
    if (old.userId)     lines.push(`👤 마케터: ${old.userId}`);
    if (old.biz_no)     lines.push(`🔢 사업자번호: ${old.biz_no}`);
    if (old.note)       lines.push(`📝 비고: ${old.note}`);
  } else {
    const FIELD_SENTENCE = {
      issue_date:    (o, n) => `발행일을 ${fmtDate(o)} 에서 ${fmtDate(n)} 으로 변경했습니다.`,
      issue_type:    (o, n) => `유형을 "${o}" 에서 "${n}" 으로 변경했습니다.`,
      customer_name: (o, n) => `상호를 "${o}" 에서 "${n}" 으로 변경했습니다.`,
      biz_no:        (o, n) => `사업자번호를 "${o}" 에서 "${n}" 으로 변경했습니다.`,
      supply_amount: (o, n) => `공급가액을 ${Number(o).toLocaleString('ko-KR')}원 에서 ${Number(n).toLocaleString('ko-KR')}원 으로 수정했습니다.`,
      tax_amount:    (o, n) => `세액을 ${Number(o).toLocaleString('ko-KR')}원 에서 ${Number(n).toLocaleString('ko-KR')}원 으로 수정했습니다.`,
      total_amount:  (o, n) => `합계금액을 ${Number(o).toLocaleString('ko-KR')}원 에서 ${Number(n).toLocaleString('ko-KR')}원 으로 수정했습니다.`,
      note:          (o, n) => `비고를 "${o || '(없음)'}" 에서 "${n || '(없음)'}" 으로 변경했습니다.`,
    };
    const oldUser = old.userId;
    const newUser = nxt.userId;
    // 기록에 마케터 칸이 없으면 안 바꾼 것 — 지운 것처럼 보이면 안 된다
    if (('userId' in nxt || 'user_id' in nxt) && oldUser !== newUser && (oldUser || newUser)) {
      lines.push(`담당 마케터를 "${oldUser || '(없음)'}" 에서 "${newUser || '(없음)'}" 으로 변경했습니다.`);
    }
    for (const [key, fn] of Object.entries(FIELD_SENTENCE)) {
      const o = old[key]; const n = nxt[key];
      if (o !== undefined && n !== undefined && String(o) !== String(n)) lines.push(fn(o, n));
    }
    lines.push(...auditNoteLines(old, nxt));
    if (!lines.length) lines.push('변경 내용 없음');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 12, color: 'var(--text-sub)', fontWeight: 600, background: 'var(--bg-subtle)', borderRadius: 6, padding: '4px 10px', letterSpacing: '0.01em' }}>
        {ctxLine}
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ fontSize: 12, color: 'var(--text-sub)', padding: '0 2px', whiteSpace: 'pre-wrap' }}>{line}</div>
      ))}
    </div>
  );
}
